import { getErrorMessage } from '@/utils/errorUtils';
import { supabase } from './client';
import { ManualPlayer } from '@/types';

/**
 * Fetch all manual players created by the current user.
 */
export const getManualPlayers = async (): Promise<ManualPlayer[]> => {
  // RLS filters by created_by = auth.uid()
  const { data, error } = await supabase
    .from('manual_players')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching manual players:', error);
    return [];
  }

  return (data || []).map(row => ({
    id: row.id,
    name: row.name,
    email: row.email,
    createdBy: row.created_by,
    createdAt: row.created_at,
  }));
};

/**
 * Create a new manual player.
 */
export const createManualPlayer = async (
  name: string,
  email?: string
): Promise<{ data?: ManualPlayer; error?: string }> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Authentication required' };

  const { data, error } = await supabase
    .from('manual_players')
    .insert({
      name: name.trim(),
      email: email?.trim().toLowerCase() || null,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating manual player:', error);
    return { error: error.message };
  }

  return {
    data: {
      id: data.id,
      name: data.name,
      email: data.email,
      createdBy: data.created_by,
      createdAt: data.created_at,
    },
  };
};

/**
 * Update a manual player's name or email.
 */
export const updateManualPlayer = async (
  id: string,
  updates: { name?: string; email?: string }
): Promise<{ data?: ManualPlayer; error?: string }> => {
  const updateData: { name?: string; email?: string | null } = {};
  if (updates.name !== undefined) updateData.name = updates.name.trim();
  if (updates.email !== undefined) updateData.email = updates.email?.trim().toLowerCase() || null;

  const { data, error } = await supabase
    .from('manual_players')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating manual player:', error);
    return { error: error.message };
  }

  return {
    data: {
      id: data.id,
      name: data.name,
      email: data.email,
      createdBy: data.created_by,
      createdAt: data.created_at,
    },
  };
};

/**
 * Delete a manual player (cascades to game_manual_players).
 */
export const deleteManualPlayer = async (id: string): Promise<{ error?: string }> => {
  const { error } = await supabase
    .from('manual_players')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting manual player:', error);
    return { error: error.message };
  }
  return {};
};

/**
 * Check if an email corresponds to an existing profile (real account).
 */
export const checkEmailExists = async (
  email: string
): Promise<{ exists: boolean; profileId?: string; name?: string }> => {
  const { data } = await supabase
    .from('profiles')
    .select('id, name, email')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();

  return {
    exists: !!data,
    profileId: data?.id,
    name: data?.name ?? undefined,
  };
};

/**
 * Merge a manual player into a real profile.
 * Transfers all game memberships, scores, and presence records.
 */
export const mergeManualPlayer = async (
  manualPlayerId: string,
  realProfileId: string
): Promise<{ error?: string }> => {
  try {
    // 1. Get all game memberships for this manual player
    const { data: gameLinks, error: linksError } = await supabase
      .from('game_manual_players')
      .select('game_id')
      .eq('manual_player_id', manualPlayerId);

    if (linksError) {
      console.error('Error fetching game links:', linksError);
      return { error: linksError.message };
    }

    // 2. Insert equivalent game_players rows (skip duplicates)
    if (gameLinks && gameLinks.length > 0) {
      const { error: insertError } = await supabase
        .from('game_players')
        .upsert(
          gameLinks.map(gl => ({ game_id: gl.game_id, player_id: realProfileId })),
          { onConflict: 'game_id,player_id', ignoreDuplicates: true }
        );

      if (insertError) {
        console.error('Error inserting game_players during merge:', insertError);
        // Continue with rest of merge
      }
    }

    // 3. Transfer scores: update user_id to real profile, clear manual_player_id
    const { error: scoreError } = await supabase
      .from('scores')
      .update({ user_id: realProfileId, manual_player_id: null })
      .eq('manual_player_id', manualPlayerId);

    if (scoreError) {
      console.error('Error transferring scores:', scoreError);
    }

    // 4. Transfer score presence
    const { error: presenceError } = await supabase
      .from('score_presence')
      .update({ profile_id: realProfileId, manual_player_id: null })
      .eq('manual_player_id', manualPlayerId);

    if (presenceError) {
      console.error('Error transferring score presence:', presenceError);
    }

    // 5. Delete game_manual_players rows
    const { error: deleteLinksError } = await supabase
      .from('game_manual_players')
      .delete()
      .eq('manual_player_id', manualPlayerId);

    if (deleteLinksError) {
      console.error('Error deleting game manual players:', deleteLinksError);
    }

    // 6. Delete the manual player record
    const { error: deleteError } = await supabase
      .from('manual_players')
      .delete()
      .eq('id', manualPlayerId);

    if (deleteError) {
      console.error('Error deleting manual player:', deleteError);
      return { error: deleteError.message };
    }

    return {};
  } catch (error) {
    console.error('Unexpected error during merge:', error);
    return { error: getErrorMessage(error) };
  }
};
